import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';
import { AppSetting } from './app-settings.types';

@Injectable({
    providedIn: 'root',
})
export class AppSettingService {
    private readonly http = inject(HttpClient);
    private readonly configState = signal<AppSetting | null>(null);
    private configLoadingPromise: Promise<AppSetting> | null = null;
    private readonly configUrl = '/settings/app.json';
    public readonly isConfigLoaded = computed(() => this.configState() !== null);

    // Public getter for the config to check if it's loaded
    get currentConfig(): AppSetting | null {
        return this.configState();
    }

    /**
     * Load application configuration from assets file
     */
    async loadConfig(): Promise<AppSetting> {
        if (this.configState()) {
            return this.configState() as AppSetting;
        }

        if (this.configLoadingPromise) {
            return this.configLoadingPromise;
        }

        this.configLoadingPromise = this.loadConfigInternal();

        try {
            return await this.configLoadingPromise;
        } finally {
            this.configLoadingPromise = null;
        }
    }

    /**
     * Ensure configuration is loaded once and shared across all callers.
     */
    async ensureConfigLoaded(): Promise<AppSetting> {
        if (this.configState()) {
            return this.configState() as AppSetting;
        }

        return this.loadConfig();
    }

    private async loadConfigInternal(): Promise<AppSetting> {
        try {
            const config$ = this.http.get<AppSetting>(this.configUrl)
                .pipe(
                    tap(config => console.info('Configuration loaded successfully')),
                    catchError(error => {
                        console.error('Failed to load configuration:', error);
                        throw error;
                    })
                );

            const config = await lastValueFrom(config$);
            this.configState.set(config);
            return config;
        } catch (error) {
            console.error('Error loading configuration:', error);
            throw new Error('Failed to load application configuration. Please check the console for details.');
        }
    }

    /**
     * Get API URL from configuration
     */
    getApiUrl(): string {
        const config = this.configState();
        if (!config?.apiBaseUrl) {
            console.warn('Configuration not loaded yet, API URL unavailable');
            return '';
        }
        return config.apiBaseUrl;
    }

    getCubeApiUrl(): string {
        const config = this.configState();
        if (!config?.cubeApiUrl) {
            console.warn('Configuration not loaded yet, using default Cube API URL');
            return '';
        }
        return config.cubeApiUrl;
    }

    /**
     * Get API version from configuration
     */
    getApiVersion(): string {
        const config = this.configState();
        if (!config?.appVersion) {
            console.warn('Configuration not loaded yet, using default API version');
            return '';
        }
        return config.appVersion;
    }
}

@Injectable({ providedIn: 'root' })
export class ApiBaseUrl {
    private appSettingService: AppSettingService = inject(AppSettingService);

    public get default(): string {
        return this.appSettingService.getApiUrl();
    }
}